import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { Button, Field, Input, Segmented } from "@/components/kit";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const MODES = [{ value: "full", label: "Plataforma inteira" }, { value: "tenants", label: "Escolher restaurantes" }];
const WORD = "RESTAURAR";

function TenantPicker({ tenants, sel, setSel }) {
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  return (
    <div className="flex flex-col max-h-56 overflow-y-auto rounded-xl border" style={{ borderColor: "var(--border)" }} data-testid="restore-tenant-list">
      {tenants.map((t) => (
        <label key={t.id} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-[var(--surface-alt)]" data-testid={`restore-tenant-${t.slug}`}>
          <input type="checkbox" checked={sel.includes(t.id)} onChange={() => toggle(t.id)} />
          <span className="flex-1 text-sm">{t.name}</span><span className="caption">{t.slug}</span>
        </label>
      ))}
    </div>
  );
}

export function RestoreDialog({ backup, onClose }) {
  const qc = useQueryClient();
  const [mode, setMode] = useState("full");
  const [sel, setSel] = useState([]);
  const [word, setWord] = useState("");
  const tenants = backup?.manifest?.tenants || [];
  const go = useMutation({
    mutationFn: () => api(`/master/platform-backups/${backup.id}/restore`, { method: "POST", body: { mode, tenant_ids: mode === "tenants" ? sel : null } }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["platform-backups"] }); toast.success("Restauração iniciada · um backup de segurança foi gerado antes"); onClose(); },
    onError: (e) => toast.error(e.message),
  });
  const ok = word.trim().toUpperCase() === WORD && (mode === "full" || sel.length > 0);
  return (
    <Dialog open={!!backup} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg bg-[var(--surface)] text-[var(--text)] border-[var(--border)] rounded-2xl" data-testid="restore-dialog">
        <DialogTitle className="flex items-center gap-2"><RotateCcw size={18} /> Restaurar backup</DialogTitle>
        <DialogDescription>Arquivo de {fmtDateTime(backup?.created_at)} · {tenants.length} restaurante(s)</DialogDescription>
        <Segmented options={MODES} value={mode} onChange={setMode} testid="restore-mode" />
        {mode === "tenants" && <TenantPicker tenants={tenants} sel={sel} setSel={setSel} />}
        <div className="rounded-xl p-3 flex gap-2 text-sm" style={{ background: "var(--danger-soft, #FBEAEA)", color: "var(--danger)" }} data-testid="restore-warning">
          <AlertTriangle size={18} className="shrink-0 mt-0.5" />
          <span>{mode === "full" ? "Todos os dados atuais da plataforma serão apagados e trocados pelos do arquivo, inclusive usuários do Painel Master." : "Os dados atuais dos restaurantes marcados serão apagados e trocados pelos do arquivo. Os outros não mudam."} Antes, um backup de segurança do estado atual é gerado automaticamente.</span>
        </div>
        <Field label={`Digite ${WORD} para confirmar`}><Input value={word} onChange={(e) => setWord(e.target.value)} placeholder={WORD} data-testid="restore-confirm-input" /></Field>
        <Button icon={RotateCcw} disabled={!ok} loading={go.isPending} onClick={() => go.mutate()} style={{ background: ok ? "var(--danger)" : undefined }} data-testid="restore-confirm-button">Restaurar agora</Button>
      </DialogContent>
    </Dialog>
  );
}
